// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface Sport {
  id: string;
  name: string;
  gender: string | null;
}

interface SportStatDefinition {
  id: string;
  sport_id: string;
  label: string;
  key: string;
  unit?: string;
}

interface StatFormData {
  label: string;
  key: string;
  unit: string;
}

const SportStatDefinitionsManagement: React.FC = () => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [statDefinitions, setStatDefinitions] = useState<SportStatDefinition[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStatDefinition, setEditingStatDefinition] = useState<SportStatDefinition | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<StatFormData>({
    label: '',
    key: '',
    unit: ''
  });

  useEffect(() => {
    console.log('🔍 Sport Stat Definitions Admin Loaded - SportStatDefinitionsManagement.tsx');
    fetchSports();
  }, []);

  useEffect(() => {
    if (selectedSportId) {
      console.log('🔍 Selected sport changed for stat definitions:', selectedSportId);
      fetchStatDefinitionsBySport(selectedSportId);
    } else {
      setStatDefinitions([]);
    }
  }, [selectedSportId]);

  const fetchSports = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Fetching sports for stat definitions dropdown...');
      
      const { data, error } = await supabase
        .from('sports')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('❌ Error fetching sports:', error);
        toast.error('Failed to load sports');
        return;
      }

      console.log('✅ Sports fetched successfully for stat definitions:', data?.length || 0, 'records');
      setSports(data || []);
    } catch (error) {
      console.error('❌ Unexpected error fetching sports:', error);
      toast.error('Failed to load sports');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatDefinitionsBySport = async (sportId: string) => {
    try {
      setIsStatsLoading(true);
      console.log('🔍 Fetching stat definitions for sport:', sportId);
      
      const { data, error } = await supabase
        .from('sport_stat_definitions')
        .select('*')
        .eq('sport_id', sportId)
        .order('label', { ascending: true });

      if (error) {
        console.error('❌ Error fetching stat definitions:', error);
        toast.error('Failed to load stat definitions');
        return;
      }

      console.log('✅ Stat definitions fetched successfully:', data?.length || 0, 'records');
      setStatDefinitions(data || []);
    } catch (error) {
      console.error('❌ Unexpected error fetching stat definitions:', error);
      toast.error('Failed to load stat definitions');
    } finally {
      setIsStatsLoading(false);
    }
  };

  const handleAddStatDefinition = async () => {
    if (!formData.label.trim()) {
      toast.error('Label is required');
      return;
    }

    if (!formData.key.trim()) {
      toast.error('Key is required');
      return;
    }

    if (!selectedSportId) {
      toast.error('Please select a sport first');
      return;
    }

    console.log('🔍 Attempting to add stat definition:', {
      label: formData.label.trim(),
      key: formData.key.trim(),
      unit: formData.unit.trim() || null,
      sport_id: selectedSportId
    });

    setIsSubmitting(true);
    try {
      const insertData = {
        label: formData.label.trim(),
        key: formData.key.trim(),
        unit: formData.unit.trim() || null,
        sport_id: selectedSportId,
      };

      console.log('🔍 Insert data prepared:', insertData);

      const { data, error } = await supabase
        .from('sport_stat_definitions')
        .insert([insertData])
        .select()
        .single();

      console.log('🔍 Supabase insert result:', { data, error });

      if (error) {
        console.error('❌ Error adding stat definition:', error);
        toast.error(`Failed to add stat definition: ${error.message}`);
        return;
      }

      console.log('✅ Stat definition added successfully');
      await fetchStatDefinitionsBySport(selectedSportId);
      setFormData({ label: '', key: '', unit: '' });
      setIsAddModalOpen(false);
      toast.success('Stat definition added successfully');
    } catch (error) {
      console.error('❌ Unexpected error adding stat definition:', error);
      toast.error('Failed to add stat definition');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStatDefinition = async () => {
    if (!editingStatDefinition || !formData.label.trim() || !formData.key.trim()) {
      toast.error('Label and key are required');
      return;
    }

    console.log('🔍 Attempting to update stat definition:', editingStatDefinition.id, 'with data:', {
      label: formData.label.trim(),
      key: formData.key.trim(),
      unit: formData.unit.trim() || null
    });

    setIsSubmitting(true);
    try {
      const updateData = {
        label: formData.label.trim(),
        key: formData.key.trim(),
        unit: formData.unit.trim() || null,
      };

      const { data, error } = await supabase
        .from('sport_stat_definitions')
        .update(updateData)
        .eq('id', editingStatDefinition.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating stat definition:', error);
        toast.error('Failed to update stat definition');
        return;
      }

      console.log('✅ Stat definition updated successfully');
      setStatDefinitions(statDefinitions.map(stat => 
        stat.id === editingStatDefinition.id ? data : stat
      ));
      setFormData({ label: '', key: '', unit: '' });
      setEditingStatDefinition(null);
      setIsEditModalOpen(false);
      toast.success('Stat definition updated successfully');
    } catch (error) {
      console.error('❌ Unexpected error updating stat definition:', error);
      toast.error('Failed to update stat definition');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStatDefinition = async (statDefinitionId: string) => {
    console.log('🔍 Attempting to delete stat definition:', statDefinitionId);
    try {
      const { error } = await supabase
        .from('sport_stat_definitions')
        .delete()
        .eq('id', statDefinitionId);

      if (error) {
        console.error('❌ Error deleting stat definition:', error);
        toast.error('Failed to delete stat definition');
        return;
      }

      console.log('✅ Stat definition deleted successfully');
      setStatDefinitions(statDefinitions.filter(stat => stat.id !== statDefinitionId));
      toast.success('Stat definition deleted successfully');
    } catch (error) {
      console.error('❌ Unexpected error deleting stat definition:', error);
      toast.error('Failed to delete stat definition');
    }
  };

  const openEditModal = (statDefinition: SportStatDefinition) => {
    setEditingStatDefinition(statDefinition);
    setFormData({
      label: statDefinition.label,
      key: statDefinition.key,
      unit: statDefinition.unit || ''
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({ label: '', key: '', unit: '' });
    setEditingStatDefinition(null);
  };

  const selectedSport = sports.find(sport => sport.id === selectedSportId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading sports...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Sport Stat Definitions Management</h2>
          <p className="text-gray-600">Manage stat definitions for each sport</p>
        </div>
      </div>

      {/* Sport Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Sport</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full max-w-sm">
            <Label htmlFor="sport-select">Sport</Label>
            <Select value={selectedSportId} onValueChange={setSelectedSportId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a sport to manage stat definitions" />
              </SelectTrigger>
              <SelectContent>
                {sports.map((sport) => (
                  <SelectItem key={sport.id} value={sport.id}>
                    {sport.name} ({sport.gender || 'Any'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stat Definitions Management */}
      {selectedSportId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Stat Definitions for {selectedSport?.name} ({statDefinitions.length})
              </CardTitle>
              <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Stat Definition
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Stat Definition</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="stat-label">Label</Label>
                      <Input
                        id="stat-label"
                        value={formData.label}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        placeholder="Enter display label (e.g., Points per Game)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="stat-key">Key</Label>
                      <Input
                        id="stat-key"
                        value={formData.key}
                        onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                        placeholder="Enter internal key (e.g., points_per_game)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="stat-unit">Unit (Optional)</Label>
                      <Input
                        id="stat-unit"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        placeholder="Enter unit (e.g., pts, %, sec)"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleAddStatDefinition} 
                        disabled={isSubmitting || !formData.label.trim() || !formData.key.trim()}
                      >
                        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Add Stat Definition
                      </Button>
                      <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {isStatsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Loading stat definitions...</span>
              </div>
            ) : statDefinitions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No stat definitions found for this sport. Add your first stat definition to get started.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statDefinitions.map((statDefinition) => (
                    <TableRow key={statDefinition.id}>
                      <TableCell className="font-medium">{statDefinition.label}</TableCell>
                      <TableCell className="font-mono text-sm">{statDefinition.key}</TableCell>
                      <TableCell>{statDefinition.unit || '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(statDefinition)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Stat Definition</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{statDefinition.label}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteStatDefinition(statDefinition.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stat Definition</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-stat-label">Label</Label>
              <Input
                id="edit-stat-label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Enter display label"
              />
            </div>
            <div>
              <Label htmlFor="edit-stat-key">Key</Label>
              <Input
                id="edit-stat-key"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                placeholder="Enter internal key"
              />
            </div>
            <div>
              <Label htmlFor="edit-stat-unit">Unit (Optional)</Label>
              <Input
                id="edit-stat-unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="Enter unit"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleEditStatDefinition} 
                disabled={isSubmitting || !formData.label.trim() || !formData.key.trim()}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Update Stat Definition
              </Button>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SportStatDefinitionsManagement;